import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Select, Upload, notification, Typography, Space, Tag, Alert } from 'antd';
import { UploadOutlined, ReloadOutlined } from '@ant-design/icons';

import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { date as fmtDate, label, money } from '../../lib/format';
const DOCUMENT_TYPES = ['INVOICE', 'RECEIPT', 'BANK_STATEMENT', 'SUPPLIER_STATEMENT', 'PAYMENT'];

/**
 * Thin client over ScanNRead's document store for one set of books: lists the
 * books' scanned documents and lets a user add a new one (uploaded, then
 * registered+OCR'd via ScanNRead's own pipeline). Nothing is stored locally -
 * this always reads through live, unlike the VAT ledger sync.
 */
export default function ScanNReadPage() {
  const { ledgerId, ledger } = useBooks();
  const [documents, setDocuments] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [notConnected, setNotConnected] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [fileList, setFileList] = React.useState([]);
  const [form] = Form.useForm();

  const fetchDocuments = () => {
    if (!ledgerId) return;
    if (ledger && !ledger.has_scan_n_read) {
      setDocuments([]);
      setNotConnected(true);
      return;
    }
    setLoading(true);
    setNotConnected(false);
    request
      .get('/scan-n-read/documents', { ledger_id: ledgerId })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setDocuments(r.data.list);
        else {
          setNotConnected(true);
          notification.error({ message: r.message });
        }
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchDocuments, [ledgerId, ledger && ledger.has_scan_n_read]);

  const handleUpload = () => {
    form.validateFields().then((values) => {
      if (!fileList.length) return notification.error({ message: 'Select a file first' });
      const formData = new FormData();
      formData.append('file', fileList[0]);
      formData.append('ledger_id', ledgerId);
      formData.append('document_type', values.document_type);
      setUploading(true);
      request
        .post('/scan-n-read/documents', formData)
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: r.message });
            setUploadOpen(false);
            setFileList([]);
            form.resetFields();
            fetchDocuments();
          } else notification.error({ message: r.message });
        })
        .finally(() => setUploading(false));
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: 'Type', dataIndex: 'document_type', render: (v) => <Tag>{label(v)}</Tag> },
    { title: 'Date', dataIndex: 'date', render: (v) => fmtDate(v) },
    { title: 'Vendor', dataIndex: ['Vendor', 'name'] },
    { title: 'Total', dataIndex: 'total', align: 'right', render: (v) => money(v) },
    { title: 'Status', dataIndex: 'check_status' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            ScanNRead
          </Typography.Title>
          <BooksCaption />
        </div>
        <Space wrap>
          <BooksSelector />
          <Button icon={<ReloadOutlined />} onClick={fetchDocuments}>
            Refresh
          </Button>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadOpen(true)} disabled={notConnected || !ledgerId}>
            Add Document
          </Button>
        </Space>
      </div>

      {notConnected && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="ScanNRead not connected"
          description={
            <>
              These books have no ScanNRead tenant configured. A manager sets the ScanNRead customer id and API token under <Link to="/books">Client Books → Settings</Link>; once saved, the documents show up here immediately.
            </>
          }
        />
      )}

      <Table rowKey="id" columns={columns} dataSource={documents} loading={loading} />

      <Modal title="Add Document" open={uploadOpen} onCancel={() => setUploadOpen(false)} onOk={handleUpload} confirmLoading={uploading}>
        <Form form={form} layout="vertical">
          <Form.Item name="document_type" label="Document Type" rules={[{ required: true }]}>
            <Select options={DOCUMENT_TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item label="File" required>
            <Upload
              beforeUpload={(file) => {
                setFileList([file]);
                return false;
              }}
              onRemove={() => setFileList([])}
              fileList={fileList.map((f, i) => ({ uid: String(i), name: f.name, status: 'done' }))}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
